import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiClock, FiPlus, FiFilter, FiCheckCircle, FiAlertCircle, FiX, 
  FiCalendar, FiGlobe, FiTrash2, FiPause, FiPlay, FiSend, FiSearch, FiMoreVertical
} = FiIcons;
import { mockScheduledEmails, mockApps, mockTemplates } from '../data/mockData';

const CreateScheduleModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    appId: '', templateId: '', recipient: '', sendAt: '', timezone: 'AEST', notes: ''
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2">
            <SafeIcon icon={FiPlus} className="text-blue-400" /> Schedule New Email
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client App</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.appId} onChange={(e) => setFormData({...formData, appId: e.target.value})}>
                <option value="">Select App</option>
                {mockApps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Template</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.templateId} onChange={(e) => setFormData({...formData, templateId: e.target.value})}>
                <option value="">Select Template</option>
                {mockTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Recipient Email</label>
            <input type="email" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" placeholder="user@example.com" value={formData.recipient} onChange={(e) => setFormData({...formData, recipient: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Send Date & Time</label>
              <input type="datetime-local" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.sendAt} onChange={(e) => setFormData({...formData, sendAt: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Timezone</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.timezone} onChange={(e) => setFormData({...formData, timezone: e.target.value})}>
                <option>AEST</option>
                <option>UTC</option>
                <option>EST</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-slate-500 hover:text-slate-700">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-8 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all">Schedule Email</button>
        </div>
      </div>
    </div>
  );
};

const ScheduledEmails = () => {
  const [schedules, setSchedules] = useState(mockScheduledEmails);
  const [activeModal, setActiveModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Sent': return 'bg-emerald-100 text-emerald-700';
      case 'Failed': return 'bg-red-100 text-red-700';
      case 'Paused': return 'bg-amber-100 text-amber-700';
      case 'Processing': return 'bg-blue-100 text-blue-700 animate-pulse';
      case 'Cancelled': return 'bg-slate-100 text-slate-500';
      default: return 'bg-blue-50 text-blue-600';
    }
  };

  const getAppName = (id) => mockApps.find(a => a.id === id)?.name || 'Unknown';
  const getTemplateName = (id) => mockTemplates.find(t => t.id === id)?.name || 'Unknown';

  const toggleStatus = (id, current) => {
    const newStatus = current === 'Paused' ? 'Scheduled' : 'Paused';
    setSchedules(schedules.map(s => s.id === id ? {...s, status: newStatus} : s));
  };

  const deleteSchedule = (id) => {
    if (confirm('Are you sure you want to cancel this scheduled email?')) {
      setSchedules(schedules.filter(s => s.id !== id));
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Scheduled Emails</h2>
          <p className="text-sm text-slate-500">Queue management for delayed or recurring deliveries.</p>
        </div>
        <button onClick={() => setActiveModal(true)} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
          <SafeIcon icon={FiPlus} /> Schedule Email
        </button>
      </div>

      <div className="flex bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input type="text" placeholder="Search recipients or apps..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
                <th className="px-6 py-4">Recipient</th>
                <th className="px-6 py-4">App & Template</th>
                <th className="px-6 py-4">Scheduled Time</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-50">
              {schedules.map(item => (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-800">{item.recipient}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs font-bold text-slate-700">{getAppName(item.appId)}</p>
                    <p className="text-[10px] text-slate-400">{getTemplateName(item.templateId)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-slate-600">
                      <SafeIcon icon={FiClock} className="text-slate-400" />
                      <span className="text-xs font-medium">{item.sendAt}</span>
                      <span className="text-[9px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-black">{item.timezone}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => toggleStatus(item.id, item.status)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
                        <SafeIcon icon={item.status === 'Paused' ? FiPlay : FiPause} />
                      </button>
                      <button onClick={() => deleteSchedule(item.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                      <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition-all">
                        <SafeIcon icon={FiMoreVertical} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {activeModal && (
        <CreateScheduleModal 
          onClose={() => setActiveModal(false)}
          onSave={(data) => {
            setSchedules([...schedules, { ...data, id: `sch_${Date.now()}`, status: 'Scheduled' }]);
            setActiveModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ScheduledEmails;